import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { 
  ChevronLeft, 
  Heart, 
  Share, 
  Star, 
  Shield, 
  MessageCircle,
  Camera,
  Clock,
  Pill,
  Trees,
  Check,
  ChevronRight
} from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { mockListings, mockReviews } from "@/data/mockData";
import { cn } from "@/lib/utils";

const featureLabels: Record<string, { label: string; icon: React.ComponentType<{ className?: string }> }> = {
  quintal: { label: "Com quintal", icon: Trees },
  cameras: { label: "Câmeras 24h", icon: Camera },
  "24h": { label: "Atenção 24h", icon: Clock },
  medicacao: { label: "Administra medicação", icon: Pill },
};

export default function ListingDetail() {
  const { id } = useParams();
  const [currentImage, setCurrentImage] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);

  const listing = mockListings.find((l) => l.id === id);

  if (!listing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Anúncio não encontrado</h1>
          <Link to="/" className="text-forest hover:underline">
            Voltar para a home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header activeTab="tutores" isCompact />

      {/* Mobile Header */}
      <div className="sticky top-[57px] z-40 bg-background border-b py-3 md:hidden">
        <div className="container flex items-center justify-between">
          <Link to="/buscar" className="p-2 -ml-2">
            <ChevronLeft className="w-6 h-6" />
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsFavorite(!isFavorite)}
              className="p-2"
            >
              <Heart className={cn("w-6 h-6", isFavorite && "fill-coral text-coral")} />
            </button>
            <button className="p-2">
              <Share className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      <main className="container py-6">
        {/* Desktop Breadcrumb */}
        <div className="hidden md:flex items-center gap-2 mb-4 text-sm">
          <Link to="/" className="text-muted-foreground hover:text-foreground">Home</Link>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
          <Link to="/buscar" className="text-muted-foreground hover:text-foreground">Buscar</Link>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
          <span className="text-foreground">{listing.title}</span>
        </div>

        {/* Title Section */}
        <div className="mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold mb-2">{listing.title}</h1>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-foreground" />
                  <span className="font-semibold">{listing.rating}</span>
                  <span className="text-muted-foreground">({listing.reviewCount} avaliações)</span>
                </div>
                <span className="text-muted-foreground">·</span>
                {listing.isSuperhost && (
                  <>
                    <span className="font-semibold">Superhost</span>
                    <span className="text-muted-foreground">·</span>
                  </>
                )}
                <span className="text-muted-foreground">{listing.neighborhood}, {listing.location}</span>
              </div>
            </div>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center gap-2">
              <Button variant="ghost" size="sm" className="rounded-full">
                <Share className="w-4 h-4 mr-2" />
                Compartilhar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="rounded-full"
                onClick={() => setIsFavorite(!isFavorite)}
              >
                <Heart className={cn("w-4 h-4 mr-2", isFavorite && "fill-coral text-coral")} />
                Salvar
              </Button>
            </div>
          </div>
        </div>

        {/* Image Gallery */}
        <div className="relative rounded-xl overflow-hidden mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-2 aspect-[16/9] md:aspect-[2/1]">
            <div className="md:col-span-2 md:row-span-2 relative">
              <img
                src={listing.images[0]}
                alt={listing.title}
                className="w-full h-full object-cover"
              />
            </div>
            {listing.images.slice(1, 5).map((img, i) => (
              <div key={i} className="hidden md:block relative">
                <img
                  src={img}
                  alt={`${listing.title} ${i + 2}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>

          {/* Mobile Image Dots */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1 md:hidden">
            {listing.images.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentImage(i)}
                className={cn(
                  "w-2 h-2 rounded-full transition-all",
                  i === currentImage ? "bg-white" : "bg-white/50"
                )}
              />
            ))}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Host Info */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">
                  Hospedagem com {listing.hostName}
                </h2>
                <p className="text-muted-foreground">
                  {listing.services.map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(" · ")}
                </p>
              </div>
              <div className="w-14 h-14 rounded-full bg-forest text-white flex items-center justify-center text-xl font-bold">
                {listing.hostName.charAt(0)}
              </div>
            </div>

            <Separator />

            {/* Features */}
            <div className="grid grid-cols-2 gap-4">
              {listing.features.map((feature) => {
                const { label, icon: Icon } = featureLabels[feature] || { label: feature, icon: Check };
                return (
                  <div key={feature} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                      <Icon className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <span className="font-medium">{label}</span>
                  </div>
                );
              })}
            </div>

            <Separator />

            {/* Description */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Sobre o espaço</h3>
              <p className="text-muted-foreground leading-relaxed">{listing.description}</p>
            </div>

            {/* Host Description */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Sobre {listing.hostName}</h3>
              <p className="text-muted-foreground leading-relaxed">{listing.hostDescription}</p>
            </div>

            <Separator />

            {/* Routine */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Rotina do pet</h3>
              <p className="text-muted-foreground leading-relaxed">{listing.routine}</p>
            </div>

            {/* Environment */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Ambiente</h3>
              <p className="text-muted-foreground leading-relaxed">{listing.environment}</p>
            </div>

            <Separator />

            {/* What's Included */}
            <div>
              <h3 className="text-lg font-semibold mb-3">O que está incluso</h3>
              <div className="grid grid-cols-2 gap-2">
                {listing.included.map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-forest" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Rules */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Regras</h3>
              <ul className="space-y-2">
                {listing.rules.map((rule, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-muted-foreground">•</span>
                    <span className="text-muted-foreground">{rule}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Health & Safety */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Saúde e segurança</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {listing.healthSafety.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 p-3 bg-forest-light rounded-lg">
                    <Shield className="w-5 h-5 text-forest" />
                    <span className="text-sm font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Reviews */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <Star className="w-6 h-6 fill-foreground" />
                <span className="text-xl font-bold">{listing.rating}</span>
                <span className="text-muted-foreground">· {listing.reviewCount} avaliações</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {mockReviews.map((review) => (
                  <div key={review.id} className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center font-semibold">
                        {review.author.charAt(0)}
                      </div>
                      <div>
                        <p className="font-semibold">{review.author}</p>
                        <p className="text-sm text-muted-foreground">{review.date}</p>
                      </div>
                    </div>
                    <p className="text-muted-foreground">{review.content}</p>
                  </div>
                ))}
              </div>

              <Button variant="outline" className="mt-6 rounded-full">
                Ver todas as {listing.reviewCount} avaliações
              </Button>
            </div>
          </div>

          {/* Right Column - Booking Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-[120px] bg-card border rounded-xl p-6 shadow-card">
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-2xl font-bold">R$ {listing.pricePerNight}</span>
                <span className="text-muted-foreground">/ noite</span>
              </div>

              {/* Date Inputs */}
              <div className="border rounded-xl mb-4 divide-y">
                <div className="grid grid-cols-2 divide-x">
                  <div className="p-3">
                    <label className="text-xs font-semibold uppercase">Check-in</label>
                    <input
                      type="text"
                      placeholder="Selecione"
                      className="w-full mt-1 text-sm bg-transparent focus:outline-none"
                    />
                  </div>
                  <div className="p-3">
                    <label className="text-xs font-semibold uppercase">Check-out</label>
                    <input
                      type="text"
                      placeholder="Selecione"
                      className="w-full mt-1 text-sm bg-transparent focus:outline-none"
                    />
                  </div>
                </div>
                <div className="p-3">
                  <label className="text-xs font-semibold uppercase">Pets</label>
                  <select className="w-full mt-1 text-sm bg-transparent focus:outline-none">
                    <option>1 pet</option>
                    <option>2 pets</option>
                    <option>3 pets</option>
                  </select>
                </div>
              </div>

              <Button className="w-full bg-coral hover:bg-coral-dark text-white py-6 text-lg font-semibold rounded-xl mb-4">
                Reservar
              </Button>

              <Button variant="outline" className="w-full py-5 rounded-xl">
                <MessageCircle className="w-5 h-5 mr-2" />
                Falar com {listing.hostName}
              </Button>

              <p className="text-center text-sm text-muted-foreground mt-4">
                Você ainda não será cobrado
              </p>

              <Separator className="my-6" />

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">R$ {listing.pricePerNight} x 3 noites</span>
                  <span>R$ {listing.pricePerNight * 3}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Taxa de serviço</span>
                  <span>R$ 25</span>
                </div>
                <Separator />
                <div className="flex justify-between font-bold">
                  <span>Total</span>
                  <span>R$ {listing.pricePerNight * 3 + 25}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
